import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  IconButton,
  Divider,
  useTheme,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  Title as TitleIcon,
  FormatListBulleted as FormatListBulletedIcon,
  FormatListNumbered as FormatListNumberedIcon,
  FormatQuote as FormatQuoteIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  TableChart as TableChartIcon,
  Difference as DifferenceIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  AutoAwesome as AutoAwesomeIcon,
  VerticalSplit as VerticalSplitIcon,
  Fullscreen as FullscreenIcon,
  ViewAgenda as ViewAgendaIcon,
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import MarkdownDiffViewer from './MarkdownDiffViewer';

interface MarkdownEditorProps {
  value: string;
  onChange?: (value: string) => void;
  originalValue?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
  height?: number | string;
  showDiffTab?: boolean;
  originalTitle?: string;
  modifiedTitle?: string;
  onOpenAiAssistant?: () => void;
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({
  value = '',
  onChange,
  originalValue,
  label = "Markdown Content",
  placeholder = "Enter markdown content here...",
  disabled = false,
  height = 550,
  showDiffTab = true,
  originalTitle = "Published Baseline",
  modifiedTitle = "Current Draft",
  onOpenAiAssistant,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [activeTab, setActiveTab] = useState<'write' | 'preview' | 'diff'>('write');
  const [writeLayout, setWriteLayout] = useState<'split' | 'editor' | 'preview'>('split');
  const [textAreaRef, setTextAreaRef] = useState<HTMLTextAreaElement | null>(null);

  const hasDiffBaseline = typeof originalValue === 'string';
  const hasChanges = hasDiffBaseline && originalValue !== value;

  // Formatting helpers to insert markdown syntax at cursor
  const insertSyntax = (before: string, after: string = '') => {
    if (!textAreaRef || !onChange) return;
    const start = textAreaRef.selectionStart;
    const end = textAreaRef.selectionEnd;
    const text = textAreaRef.value;
    const selection = text.substring(start, end);
    const replacement = `${before}${selection || 'text'}${after}`;
    const newValue = text.substring(0, start) + replacement + text.substring(end);
    onChange(newValue);

    setTimeout(() => {
      textAreaRef.focus();
      textAreaRef.setSelectionRange(
        start + before.length,
        start + before.length + (selection ? selection.length : 4)
      );
    }, 0);
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        width: '100%',
      }}
    >
      {/* Top Header with Tabs, Markdown Toolbar & Layout Toggle */}
      <Box
        sx={{
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
          px: 2,
          py: 0.8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Tabs
            value={activeTab}
            onChange={(_e, val) => setActiveTab(val)}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none', fontWeight: 600 } }}
          >
            <Tab
              value="write"
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <EditIcon sx={{ fontSize: 16 }} />
                  <span>{disabled ? "Editor / Split View" : "Write"}</span>
                </Stack>
              }
            />
            <Tab
              value="preview"
              label={
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <VisibilityIcon sx={{ fontSize: 16 }} />
                  <span>Full Preview</span>
                </Stack>
              }
            />
            {showDiffTab && hasDiffBaseline && (
              <Tab
                value="diff"
                label={
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <DifferenceIcon sx={{ fontSize: 16 }} />
                    <span>Diff / Changes</span>
                    {hasChanges && (
                      <Chip
                        label="Modified"
                        size="small"
                        color="warning"
                        sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                }
              />
            )}
          </Tabs>

          {/* In Write Tab: Layout toggle (Split / Full Editor / Preview) */}
          {activeTab === 'write' && (
            <ToggleButtonGroup
              size="small"
              value={writeLayout}
              exclusive
              onChange={(_e, val) => val && setWriteLayout(val)}
              sx={{
                height: 28,
                ml: { xs: 0, sm: 1 },
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  fontWeight: 600,
                  gap: 0.5,
                },
              }}
            >
              <ToggleButton value="split" aria-label="Split View">
                <VerticalSplitIcon sx={{ fontSize: 14 }} />
                <span>Split (50/50)</span>
              </ToggleButton>
              <ToggleButton value="editor" aria-label="Full Width Editor">
                <FullscreenIcon sx={{ fontSize: 14 }} />
                <span>Full Width Editor</span>
              </ToggleButton>
              <ToggleButton value="preview" aria-label="Preview Only">
                <ViewAgendaIcon sx={{ fontSize: 14 }} />
                <span>Preview Only</span>
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>

        {/* Right Action buttons: Delphi AI trigger + Markdown Formatting */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {onOpenAiAssistant && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
              onClick={onOpenAiAssistant}
              sx={{
                height: 30,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderRadius: 1.5,
                borderColor: theme.palette.primary.main,
                background: isDark
                  ? 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.15) 100%)'
                  : 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(168,85,247,0.08) 100%)',
                '&:hover': {
                  background: isDark
                    ? 'linear-gradient(135deg, rgba(99,102,241,0.25) 0%, rgba(168,85,247,0.25) 100%)'
                    : 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.15) 100%)',
                },
              }}
            >
              Delphi AI Refine
            </Button>
          )}

          {/* Toolbar visible in Write mode if not disabled */}
          {activeTab === 'write' && !disabled && (
            <Stack direction="row" spacing={0.3} alignItems="center" flexWrap="wrap">
              <Tooltip title="Heading 2">
                <IconButton size="small" onClick={() => insertSyntax('## ', '')}>
                  <TitleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Bold">
                <IconButton size="small" onClick={() => insertSyntax('**', '**')}>
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic">
                <IconButton size="small" onClick={() => insertSyntax('*', '*')}>
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.5 }} />
              <Tooltip title="Bullet List">
                <IconButton size="small" onClick={() => insertSyntax('- ', '')}>
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Numbered List">
                <IconButton size="small" onClick={() => insertSyntax('1. ', '')}>
                  <FormatListNumberedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Quote">
                <IconButton size="small" onClick={() => insertSyntax('> ', '')}>
                  <FormatQuoteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Code Block">
                <IconButton size="small" onClick={() => insertSyntax('```\n', '\n```')}>
                  <CodeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Link">
                <IconButton size="small" onClick={() => insertSyntax('[', '](https://)')}>
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Table">
                <IconButton
                  size="small"
                  onClick={() =>
                    insertSyntax(
                      '\n| Header 1 | Header 2 |\n|----------|----------|\n| Cell 1   | Cell 2   |\n'
                    )
                  }
                >
                  <TableChartIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Box>

      {/* Main Tab Content */}
      <Box sx={{ minHeight: height }}>
        {activeTab === 'write' && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns:
                writeLayout === 'editor'
                  ? '1fr'
                  : writeLayout === 'preview'
                  ? '1fr'
                  : { xs: '1fr', md: '1fr 1fr' },
              minHeight: height,
              bgcolor: 'background.paper',
              width: '100%',
            }}
          >
            {/* Editor Textarea Pane */}
            {(writeLayout === 'split' || writeLayout === 'editor') && (
              <Box
                sx={{
                  p: 2,
                  borderRight:
                    writeLayout === 'split'
                      ? { md: `1px solid ${theme.palette.divider}` }
                      : 'none',
                  borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {label} (Markdown Source)
                  </Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.7rem' }}>
                    {value ? `${value.length} chars • ~${value.trim().split(/\s+/).filter(Boolean).length} words` : '0 words'}
                  </Typography>
                </Stack>
                <TextField
                  inputRef={(ref) => setTextAreaRef(ref)}
                  fullWidth
                  multiline
                  rows={24}
                  value={value}
                  onChange={(e) => onChange && onChange(e.target.value)}
                  placeholder={placeholder}
                  disabled={disabled}
                  variant="outlined"
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      p: 0,
                      alignItems: 'stretch',
                      width: '100%',
                    },
                    '& .MuiInputBase-input': {
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                      fontSize: '0.875rem',
                      lineHeight: 1.65,
                      p: 2,
                      width: '100% !important',
                      minWidth: '100%',
                      boxSizing: 'border-box',
                      flex: 1,
                      height: '100% !important',
                      minHeight: typeof height === 'number' ? `${height - 90}px` : '480px',
                      overflowY: 'auto !important',
                      resize: 'vertical',
                    },
                  }}
                />
              </Box>
            )}

            {/* Live Preview Pane */}
            {(writeLayout === 'split' || writeLayout === 'preview') && (
              <Box
                sx={{
                  p: 2.5,
                  overflow: 'auto',
                  maxHeight: '75vh',
                  minHeight: height,
                  bgcolor: isDark ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.01)',
                  boxSizing: 'border-box',
                  width: '100%',
                }}
              >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                Live Formatted Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box
                sx={{
                  lineHeight: 1.75,
                  fontSize: '0.95rem',
                  '& h1, & h2, & h3, & h4': {
                    mt: 2,
                    mb: 1,
                    fontWeight: 700,
                  },
                  '& p': { mb: 1.5 },
                  '& blockquote': {
                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                    pl: 2,
                    py: 0.5,
                    my: 1.5,
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    fontStyle: 'italic',
                  },
                  '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    my: 2,
                    '& th, & td': {
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1,
                      textAlign: 'left',
                    },
                    '& th': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                      fontWeight: 600,
                    },
                  },
                  '& code': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    px: 0.6,
                    py: 0.2,
                    borderRadius: 0.5,
                    fontSize: '0.85em',
                  },
                  '& pre': {
                    bgcolor: isDark ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.04)',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                  },
                }}
              >
                {value ? (
                  <ReactMarkdown>{value}</ReactMarkdown>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Nothing to preview. Start typing markdown in the editor.
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

        {activeTab === 'preview' && (
          <Box sx={{ p: 4, maxWidth: 900, mx: 'auto', lineHeight: 1.8 }}>
            <ReactMarkdown>{value || '_No content_'}</ReactMarkdown>
          </Box>
        )}

        {activeTab === 'diff' && hasDiffBaseline && (
          <MarkdownDiffViewer
            original={originalValue}
            modified={value}
            originalTitle={originalTitle}
            modifiedTitle={modifiedTitle}
            height={height}
          />
        )}
      </Box>
    </Box>
  );
};

export default MarkdownEditor;